import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { confirmDelete } from "@/lib/confirm";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, Check, X, ImagePlus } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";

const suggestCode = (name) => (name || "").replace(/[^A-Za-z]/g, "").slice(0, 3).toUpperCase();

function BrandRow({ wid, b, onChanged }) {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ name: b.name, code: b.code });
  const fileRef = useRef();
  const [ver, setVer] = useState(b.logo_path || "");
  const save = async () => {
    try { await api.put(`/workspaces/${wid}/brands/${b.id}`, form); toast.success("Brand updated"); setEdit(false); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post(`/workspaces/${wid}/brands/${b.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setVer(data.logo_path); toast.success("Logo updated"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async () => {
    if (!(await confirmDelete({ message: `Delete the brand "${b.name}"?` }))) return;
    try { await api.delete(`/workspaces/${wid}/brands/${b.id}`); toast.success("Brand deleted"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div data-testid={`brand-row-${b.id}`} className="flex items-center gap-3 rounded-2xl border border-[#EEEFEF] bg-white px-3.5 py-2.5">
      <button data-testid={`brand-logo-${b.id}`} onClick={() => fileRef.current?.click()} title="Upload logo" className="relative group shrink-0">
        <BrandMark key={ver} id={b.logo_path || ver ? b.id : null} name={b.name} version={ver} size={36} className="rounded-xl" />
        <span className="absolute inset-0 rounded-xl bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"><ImagePlus className="h-3.5 w-3.5 text-white" /></span>
      </button>
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0])} />
      {edit ? (
        <div className="flex-1 flex items-center gap-2">
          <Input data-testid={`brand-edit-name-${b.id}`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-8 bg-[#F6F6FD] border-[#EEEFEF]" />
          <Input data-testid={`brand-edit-code-${b.id}`} value={form.code} disabled={b.project_count > 0} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="h-8 w-20 bg-[#F6F6FD] border-[#EEEFEF] font-mono-data uppercase" />
          <button data-testid={`brand-save-${b.id}`} onClick={save} className="text-[#1f9d55]"><Check className="h-4 w-4" /></button>
          <button onClick={() => { setEdit(false); setForm({ name: b.name, code: b.code }); }} className="text-[#9FA1A4]"><X className="h-4 w-4" /></button>
        </div>
      ) : (
        <>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-[#06060C] truncate">{b.name}</p>
            <p className="font-mono-data text-[11px] text-[#9FA1A4]">{b.code}-XXXX · {b.project_count} project{b.project_count !== 1 ? "s" : ""}</p>
          </div>
          <button data-testid={`brand-edit-${b.id}`} onClick={() => setEdit(true)} className="text-[#9FA1A4] hover:text-[#723FFF]"><Pencil className="h-4 w-4" /></button>
          <button data-testid={`brand-delete-${b.id}`} onClick={del} disabled={b.project_count > 0} title={b.project_count > 0 ? "Brands with projects can't be deleted" : "Delete"} className="text-[#c4c5cc] hover:text-rose-600 disabled:opacity-40 disabled:hover:text-[#c4c5cc]"><Trash2 className="h-4 w-4" /></button>
        </>
      )}
    </div>
  );
}

export function BrandsManager({ wid, clientName }) {
  const [brands, setBrands] = useState(null);
  const [form, setForm] = useState({ name: "", code: "" });
  const [codeTouched, setCodeTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const load = () => api.get(`/workspaces/${wid}/brands`).then(({ data }) => setBrands(data.brands || [])).catch(() => setBrands([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wid]);
  const add = async () => {
    setSaving(true);
    try { await api.post(`/workspaces/${wid}/brands`, form); toast.success("Brand added"); setForm({ name: "", code: "" }); setCodeTouched(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-4" data-testid="brands-manager">
      <p className="text-xs text-[#9FA1A4]">Once a client has brands, everyone creating a project picks a brand first, or {clientName || "the client"} itself. The brand code starts the project code, e.g. <span className="font-mono-data text-[#595D62]">LOL-261001</span>.</p>
      <div className="rounded-2xl border border-[#EEEFEF] bg-[#FCFCFE] p-3.5 space-y-2.5">
        <div className="flex gap-2">
          <Input data-testid="brand-new-name" value={form.name} placeholder="Brand name, e.g. Lolli" onChange={(e) => setForm({ name: e.target.value, code: codeTouched ? form.code : suggestCode(e.target.value) })} className="bg-white border-[#EEEFEF]" />
          <Input data-testid="brand-new-code" value={form.code} placeholder="LOL" maxLength={5} onChange={(e) => { setCodeTouched(true); setForm({ ...form, code: e.target.value.toUpperCase() }); }} className="w-24 bg-white border-[#EEEFEF] font-mono-data uppercase" />
        </div>
        <div className="flex justify-end"><Button data-testid="brand-add" size="sm" onClick={add} disabled={saving || !form.name.trim() || form.code.length < 2} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" /> Add brand</>}</Button></div>
      </div>
      {brands === null ? <div className="py-6 text-center"><Loader2 className="h-4 w-4 animate-spin text-[#c4c5cc] mx-auto" /></div>
        : brands.length === 0 ? <p className="text-sm text-[#9FA1A4] text-center py-4" data-testid="brands-empty">No brands yet. Projects use the client's own code.</p>
        : <div className="space-y-2">{brands.map((b) => <BrandRow key={b.id} wid={wid} b={b} onChanged={load} />)}</div>}
    </div>
  );
}
